import { cn } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { m } from '@sohanscript/i18n';
import { parseTags } from '@sohanscript/lib/utils/format';
import {
	MarkdownContent,
	type RenderImageProps,
} from '@sohanscript/markdown/pre/markdown';
import { Chip } from '@sohanscript/ui/chip';
import { Safari } from '@sohanscript/ui/mock/safari';
import { Skeleton } from '@sohanscript/ui/skeleton';
import { WithTooltip } from '@sohanscript/ui/tooltip';
import { ClientOnly, useLocation, useParams } from '@tanstack/react-router';
import { useLenis } from 'lenis/react';
import type * as React from 'react';
import { useLayoutEffect } from 'react';
import { api } from '#/lib/trpc/client.ts';
import { Img } from '../image';
import { Link } from '../link';
import { Button } from '../ui/button';
import { ThumbGallery } from './thumb-gallery';

const renderWorkImage = (props: RenderImageProps) => (
	<Img
		{...props}
		width={typeof props.width === 'number' ? props.width : undefined}
		height={typeof props.height === 'number' ? props.height : undefined}
		placeholder="shimmer"
	/>
);

type WorkDetailsProps = React.ComponentProps<'div'>;

export function WorkDetails({ className, ...props }: WorkDetailsProps) {
	const { slug } = useParams({ from: '/_main/work/$slug' });
	const { state } = useLocation();
	const lenis = useLenis();
	const [project] = api.project.bySlug.useSuspenseQuery({ slug });
	const [description] = api.project.descriptionBySlug.useSuspenseQuery({
		slug,
	});

	const images = [...(project.isMobile ? [] : [project.url])]
		.concat(project.gallery?.map((g) => g.url) ?? [])
		.filter(Boolean);
	const tags = parseTags(project.tags);

	useLayoutEffect(() => {
		if (state?.transitionName !== 'vt-work') return;
		lenis?.scrollTo(0, { immediate: true, force: true });
		window.scrollTo(0, 0);
	}, [state?.transitionName, lenis]);

	return (
		<div className={cn(className)} {...props}>
			<div className={cn('grid gap-12 lg:grid-cols-[1fr_280px]')}>
				<div className="flex flex-col gap-8">
					<ClientOnly
						fallback={
							<div className="flex flex-col gap-3">
								<Safari className="relative" url={project.liveLink}>
									<Skeleton className="aspect-safari w-full" />
									<Skeleton className="absolute right-2 bottom-2 z-10 h-10 w-26" />
								</Safari>
								<div className="flex gap-3">
									<Skeleton className="h-5 w-20" />
									<Skeleton className="h-5 w-30" />
								</div>
								<div className="flex gap-3">
									<Skeleton className="aspect-video h-20" />
									<Skeleton className="aspect-video h-20" />
									<Skeleton className="aspect-video h-20 max-md:hidden" />
								</div>
							</div>
						}
					>
						<ThumbGallery
							isMobile={project.isMobile}
							url={project.liveLink}
							images={images}
							transitionName={
								state?.transitionName === 'vt-work' ? `vt-work-${slug}` : undefined
							}
						/>
					</ClientOnly>

					{description && (
						<div className="prose prose-invert max-w-none text-foreground/80">
							<h2 className="mb-4 font-semibold text-primary text-xl">
								{m.wd_about()}
							</h2>
							<MarkdownContent
								content={description}
								renderImage={renderWorkImage}
							/>
						</div>
					)}
				</div>

				<aside className="auto-stick-to-nav flex flex-col gap-6 max-lg:static max-lg:top-auto lg:self-start">
					<h1 className="font-bold text-2xl text-primary">{project.title}</h1>

					{tags.length > 0 && (
						<div className="flex flex-wrap gap-2">
							{tags.map((tag) => (
								<Chip key={tag}>{tag}</Chip>
							))}
						</div>
					)}

					{project.features && project.features.length > 0 && (
						<div>
							<h3 className="mb-3 font-medium text-primary text-sm uppercase tracking-wide">
								{m.wd_features()}
							</h3>
							<ul className="space-y-2">
								{project.features.map((f) => (
									<li
										key={f}
										className="flex items-start gap-2 text-foreground/80 text-sm"
									>
										<Iconify
											icon="solar:check-circle-linear"
											className="mt-0.5 size-4 shrink-0 text-primary"
										/>
										{f}
									</li>
								))}
							</ul>
						</div>
					)}

					<div className="grid grid-cols-2 gap-3">
						{project.isMobile ? (
							<Button
								href="https://expo.dev/go"
								target="_blank"
								className="w-full justify-center"
							>
								{m.wd_preview()}
								<Iconify icon="cib:expo" className="size-5" />
							</Button>
						) : (
							project.liveLink && (
								<Button
									href={project.liveLink}
									target="_blank"
									className="w-full justify-center"
								>
									{m.wd_go_live()}
									<Iconify icon="proicons:open" className="size-5" />
								</Button>
							)
						)}
						{project.github && (
							<Button
								variant="outline"
								href={project.github}
								target="_blank"
								className="w-full justify-center"
								icon="mdi:github"
							>
								{project.github === 'https://github.com/sohanemon'
									? m.wd_restricted()
									: m.wd_github()}
							</Button>
						)}
					</div>

					{project.contributors && project.contributors.length > 0 && (
						<div>
							<h3 className="mb-3 font-medium text-primary text-sm uppercase tracking-wide">
								{m.wd_contributors()}
							</h3>
							<div className="flex flex-wrap gap-2">
								{project.contributors?.map((el) => (
									<WithTooltip content={el} key={el}>
										<Link
											to={`https://github.com/${el}`}
											target="_blank"
											aria-label={m.wd_view_profile({ name: el })}
										>
											<Img
												className="w-8 rounded-full ring-ring transition-shadow hover:ring-1"
												src={`https://github.com/${el}.png`}
											/>
										</Link>
									</WithTooltip>
								))}
							</div>
						</div>
					)}
				</aside>
			</div>
		</div>
	);
}
